/** What the tolerance needs from the journey's session: its length, a sign of life, its lock. */
export interface ExitToleranceStore {
  readonly session: {
    readonly inactivityMilliseconds: number;
    touch(): void;
    lock(byItself?: boolean): void;
  };
}

export interface ExitToleranceClock {
  readonly now: () => number;
  readonly setTimeout: (callback: () => void, delay: number) => unknown;
  readonly clearTimeout: (handle: unknown) => void;
}

const SYSTEM_CLOCK: ExitToleranceClock = {
  now: () => Date.now(),
  setTimeout: (callback, delay) => setTimeout(callback, delay),
  clearTimeout: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
};

/**
 * Leaving the app is not, by itself, a reason to ask for the phrase again. When the page is hidden
 * — another app, another window, the mailbox for a code, the share sheet, the print dialog, the
 * screen turned off — a countdown starts, as long as the session's own inactivity (15 minutes):
 * one length for every absence, from the one source, so the two cannot drift apart (Yaya's
 * decision of 16/10, for comfort; what protects an unattended phone is its own screen lock, the
 * app hidden in the app switcher and, in the apps, the fingerprint or the face).
 *
 * Leaving and coming back in time both count as signs of life, so the inactivity never cuts the
 * absence short. Past the fifteen minutes the space closes, even while still away, and a return
 * the countdown could not see (a phone asleep) is measured on the clock. The page going for good
 * (`pagehide` with no back-forward cache) closes the space at once: the phrase only ever lived in
 * memory.
 */
export class ExitTolerance {
  private hiddenAt: number | null = null;
  private timer: unknown = null;
  private watching = false;
  private readonly onVisibility = (): void => this.visibilityChanged();
  private readonly onPageHide = (event: Event): void => {
    if (!(event as PageTransitionEvent).persisted) this.store.session.lock(true);
  };

  constructor(
    private readonly store: ExitToleranceStore,
    private readonly documentRef: Document,
    private readonly clock: ExitToleranceClock = SYSTEM_CLOCK,
  ) {}

  /** Every exit counts from now on: set up once, by the journey. */
  watch(): void {
    if (this.watching) return;
    this.watching = true;
    this.documentRef.addEventListener('visibilitychange', this.onVisibility);
    this.documentRef.defaultView?.addEventListener('pagehide', this.onPageHide);
  }

  /** The journey is gone: nothing is watched any more. */
  stop(): void {
    this.cancel();
    this.watching = false;
    this.documentRef.removeEventListener('visibilitychange', this.onVisibility);
    this.documentRef.defaultView?.removeEventListener('pagehide', this.onPageHide);
  }

  private get allowed(): number {
    return this.store.session.inactivityMilliseconds;
  }

  private visibilityChanged(): void {
    if (this.documentRef.visibilityState !== 'visible') {
      if (this.hiddenAt !== null) return;
      this.hiddenAt = this.clock.now();
      // Leaving is the last sign of life: the inactivity counts from here, as the absence does.
      this.store.session.touch();
      this.timer = this.clock.setTimeout(() => this.close(), this.allowed);
      return;
    }
    if (this.hiddenAt === null) return;
    const away = this.clock.now() - this.hiddenAt;
    this.cancel();
    if (away > this.allowed) this.store.session.lock(true);
    // Back in time is a sign of life too: the owner has the full fifteen minutes again.
    else this.store.session.touch();
  }

  private close(): void {
    this.cancel();
    this.store.session.lock(true);
  }

  private cancel(): void {
    if (this.timer !== null) this.clock.clearTimeout(this.timer);
    this.timer = null;
    this.hiddenAt = null;
  }
}

const tolerances = new WeakMap<object, ExitTolerance>();

/** The journey's one tolerance, created the first time it is asked for. */
export const exitToleranceFor = (
  store: ExitToleranceStore,
  documentRef: Document,
): ExitTolerance => {
  let tolerance = tolerances.get(store);
  if (tolerance === undefined) {
    tolerance = new ExitTolerance(store, documentRef);
    tolerances.set(store, tolerance);
  }
  return tolerance;
};
